import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DotLegalButton } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import LinkedControlsBox from "../../policy/policySections/LinkedControlsBox";
import PolicyControlsPicker from "../../policy/policySections/PolicyControlsDialog/PolicyControlsPicker";
import { PolicyControl } from "../../policy/policySections/PolicySections.types";
import { resolveControls } from "../../policy/policySections/PolicySections.utils";

export interface LinkedControlsSectionProps {
    linkedControlIds: Array<string>;
    availableControls: Array<PolicyControl>;
    onChange: (controlIds: Array<string>) => void;
    disabled?: boolean;
    // Translation key for the box heading; passed through to LinkedControlsBox so each host can say what its
    // links mean (existing coverage vs a plan's own scope).
    heading?: string;
    noBorder?: boolean;
    noPadding?: boolean;
}

// The "Tilknyttede kontroller" section shown under the probability section of the assess dialog and at the
// bottom of the mitigation dialog. Wraps the shared LinkedControlsBox, showing the linked-control chips with an
// Add button; clicking Add expands the picker table inline within the same box (pending selection committed via
// Add selected, discarded via Cancel). Read-only (no add button, no chip remove) when disabled.
function LinkedControlsSection(props: LinkedControlsSectionProps) {
    const { translateString } = useTranslation();
    const [isPicking, setIsPicking] = useState(false);
    const [pendingIds, setPendingIds] = useState<Array<string>>(props.linkedControlIds);

    // The section sits at the bottom of its host dialog, so expanding the picker grows the dialog past the fold
    // and leaves the picker (and its Add selected button) below the visible area until the user scrolls there.
    // Pulling the button row into view makes the thing the click just opened the thing on screen.
    const expandedEndRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (isPicking) expandedEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [isPicking]);

    const linkedControls = resolveControls(props.linkedControlIds, props.availableControls);

    const startPicking = () => {
        setPendingIds(props.linkedControlIds);
        setIsPicking(true);
    };

    const addSelected = () => {
        props.onChange(pendingIds);
        setIsPicking(false);
    };

    if (isPicking) {
        return (
            <LinkedControlsBox controls={[]} heading={props.heading} noBorder={props.noBorder} noPadding={props.noPadding}>
                <Box sx={{ display: "flex", flexDirection: "column", height: 360, minHeight: 0 }}>
                    <PolicyControlsPicker availableControls={props.availableControls} selectedIds={pendingIds} onSelectedIdsChange={setPendingIds} />
                </Box>
                <Box ref={expandedEndRef} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <DotLegalButton buttonType="secondary" size="small" onClick={() => setIsPicking(false)}>
                        {translateString("cancel")}
                    </DotLegalButton>
                    <DotLegalButton buttonType="primary" size="small" onClick={addSelected}>
                        {translateString("addSelected")}
                    </DotLegalButton>
                </Box>
            </LinkedControlsBox>
        );
    }

    return (
        <LinkedControlsBox
            noPadding={props.noPadding}
            noBorder={props.noBorder}
            controls={linkedControls}
            heading={props.heading}
            onRemove={props.disabled ? undefined : (controlId) => props.onChange(props.linkedControlIds.filter((id) => id !== controlId))}
            headerAction={
                !props.disabled && (
                    <DotLegalButton buttonType="secondary" size="small" startIcon={<AddIcon />} onClick={startPicking}>
                        {translateString("selectControls")}
                    </DotLegalButton>
                )
            }
        />
    );
}

export default LinkedControlsSection;
